import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "@phosphor-icons/react";

const HERO_IMG = "/images/hero-copertina.webp";

const lines = [
  { text: "DIAMO FORMA", gradient: false },
  { text: "E COLORE", gradient: true },
  { text: "ALLE TUE IDEE.", gradient: false },
];

const IntroOverlay = () => (
  <motion.div
    initial={{ y: 0 }}
    animate={{ y: "-100%" }}
    transition={{ duration: 0.9, delay: 1.1, ease: [0.76, 0, 0.24, 1] }}
    className="fixed inset-0 z-[100] bg-white flex items-center justify-center pointer-events-none"
    data-testid="intro-overlay"
  >
    <motion.img
      src="/images/logo.png"
      alt="I&G Grafiche"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="w-44 sm:w-60 h-auto"
    />
  </motion.div>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 260]);
  const fade = useTransform(scrollY, [0, 600], [1, 0.2]);

  return (
    <section id="top" className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <IntroOverlay />
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Team I&G Grafiche installa la grande insegna sulla facciata"
          className="w-full h-[130%] object-cover" style={{ objectPosition: "0% 0%" }} data-testid="hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/45 to-white/15" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40">
        <div className="overflow-hidden mb-6">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
            className="inline-block text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase text-zinc-900 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full border border-zinc-200"
            data-testid="hero-overline"
          >
            Stampa · Allestimenti · Comunicazione Visiva
          </motion.p>
        </div>

        <h1 className="font-display font-semibold tracking-tight leading-[1.12] text-5xl sm:text-7xl lg:text-8xl text-zinc-900" style={{ wordSpacing: "0.18em" }} data-testid="hero-title">
          {lines.map((l, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 1.55 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                className={`block ${l.gradient ? "text-gradient" : ""}`}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="overflow-hidden mt-8 max-w-2xl">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, delay: 2.05, ease: [0.16, 1, 0.3, 1] }}
            className="text-base sm:text-lg text-zinc-600 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Trasformiamo la tua presenza in un'esperienza visiva impossibile da ignorare.
            Quando l'immagine del tuo brand deve dominare la scena, I&amp;G Grafiche è la risposta.
            Prendiamo in carico ogni fase — dalla produzione agli allestimenti più complessi —
            assicurandoti una gestione lineare e senza complicazioni. Tu definisci gli obiettivi di business,
            noi creiamo lo spettacolo visivo che cattura i tuoi clienti.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.3, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a href="#contatti" data-testid="hero-cta-quote"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#FF007F] text-black font-semibold text-sm tracking-wide hover:scale-[1.04] transition-transform duration-300 shadow-lg shadow-fuchsia-500/20">
            Richiedi un Preventivo
            <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform duration-300" />
          </a>
          <a href="#servizi" data-testid="hero-cta-services"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-zinc-300 text-sm font-medium text-zinc-900 bg-white/60 hover:border-[#FF007F] hover:text-[#FF007F] transition-colors duration-300 backdrop-blur-sm">
            Scopri i Servizi
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.8, duration: 1 }}
        className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-3 text-zinc-400"
        aria-hidden="true"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase rotate-90 origin-center translate-y-[-8px]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
};
